/**
 * Thin fetch wrappers for the `/api/check-in-terms-templates` endpoints.
 *
 * All endpoints are authenticated; admin auth is enforced server-side for
 * the mutating verbs. The list endpoint is open to any logged-in user so
 * the check-in preview / public intake can hydrate the active template.
 */

import type {
  CheckInTermsIntakeType,
  CheckInTermsTemplate,
  CheckInTermsTemplateCreate,
  CheckInTermsTemplateUpdate,
  CheckInTermsTemplatesListResponse,
} from '@techgitter/contracts';

const API_BASE = '/api';

async function unwrap<T>(response: Response, action: string): Promise<T> {
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `${action} failed: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchCheckInTermsTemplates(
  intakeType?: CheckInTermsIntakeType,
  signal?: AbortSignal,
): Promise<CheckInTermsTemplate[]> {
  const qs = intakeType ? `?intakeType=${encodeURIComponent(intakeType)}` : '';
  const response = await fetch(`${API_BASE}/check-in-terms-templates${qs}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await unwrap<CheckInTermsTemplatesListResponse>(
    response,
    'Fetch templates',
  );
  return body.items ?? [];
}

export async function createCheckInTermsTemplate(
  payload: CheckInTermsTemplateCreate,
  signal?: AbortSignal,
): Promise<CheckInTermsTemplate> {
  const response = await fetch(`${API_BASE}/check-in-terms-templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  return unwrap<CheckInTermsTemplate>(response, 'Create template');
}

export async function updateCheckInTermsTemplate(
  id: string,
  payload: CheckInTermsTemplateUpdate,
  signal?: AbortSignal,
): Promise<CheckInTermsTemplate> {
  const response = await fetch(`${API_BASE}/check-in-terms-templates/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  return unwrap<CheckInTermsTemplate>(response, 'Update template');
}

export async function deleteCheckInTermsTemplate(
  id: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE}/check-in-terms-templates/${id}`, {
    method: 'DELETE',
    signal,
  });
  await unwrap<{ deleted: boolean }>(response, 'Delete template');
}

export async function setDefaultCheckInTermsTemplate(
  id: string,
  signal?: AbortSignal,
): Promise<CheckInTermsTemplate> {
  const response = await fetch(
    `${API_BASE}/check-in-terms-templates/${id}/set-default`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
    },
  );
  return unwrap<CheckInTermsTemplate>(response, 'Set default template');
}
